import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LiveTicker from '../components/LiveTicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TrendingUp, TrendingDown, Search, Grid, List, ChevronRight } from 'lucide-react';
import { securitiesAPI } from '../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Securities = () => {
  const location = useLocation();
  const [viewType, setViewType] = useState('grid');
  const [securityType, setSecurityType] = useState(
    location.pathname.includes('/movies') ? 'movie' :
    location.pathname.includes('/stars') ? 'star' : 'all'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('price');
  const [securities, setSecurities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSecurities();
  }, [securityType, sortBy, page, searchQuery]);

  const fetchSecurities = async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, order: 'desc', page, limit: 20 };
      if (securityType !== 'all') params.security_type = securityType;
      if (searchQuery) params.search = searchQuery;
      const res = await securitiesAPI.getAll(params);
      setSecurities(res.data.securities || []);
      setTotal(res.data.total || 0);
    } catch {
      setSecurities([]);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      <Header />
      <LiveTicker />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-[#F8FAFC] text-3xl sm:text-4xl font-medium tracking-tight mb-2" data-testid="securities-title">Securities</h1>
            <p className="text-[#94A3B8]">Browse and trade {total} securities</p>
          </div>
        </div>

        <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Input
                type="text"
                placeholder="Search securities..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="bg-[#0A0E17] border-[#1E293B] text-[#F8FAFC] pl-10 focus:ring-[#F59E0B]"
                data-testid="securities-search"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            </div>
            <Select value={securityType} onValueChange={(v) => { setSecurityType(v); setPage(1); }}>
              <SelectTrigger className="w-[160px] bg-[#0A0E17] border-[#1E293B] text-[#F8FAFC]" data-testid="securities-type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#121826] border-[#1E293B]">
                <SelectItem value="all" className="text-[#94A3B8] focus:bg-[#1E293B] focus:text-white">All Types</SelectItem>
                <SelectItem value="movie" className="text-[#94A3B8] focus:bg-[#1E293B] focus:text-white">MovieStocks</SelectItem>
                <SelectItem value="star" className="text-[#94A3B8] focus:bg-[#1E293B] focus:text-white">StarBonds</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[160px] bg-[#0A0E17] border-[#1E293B] text-[#F8FAFC]" data-testid="securities-sort">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#121826] border-[#1E293B]">
                <SelectItem value="price" className="text-[#94A3B8] focus:bg-[#1E293B] focus:text-white">Price</SelectItem>
                <SelectItem value="change_percent" className="text-[#94A3B8] focus:bg-[#1E293B] focus:text-white">% Change</SelectItem>
                <SelectItem value="volume" className="text-[#94A3B8] focus:bg-[#1E293B] focus:text-white">Volume</SelectItem>
                <SelectItem value="name" className="text-[#94A3B8] focus:bg-[#1E293B] focus:text-white">Name</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex bg-[#0A0E17] rounded-lg p-1 border border-[#1E293B]">
              <button onClick={() => setViewType('grid')} className={`p-2 rounded ${viewType === 'grid' ? 'bg-[#F59E0B] text-black' : 'text-[#64748B] hover:text-white'}`} data-testid="view-grid-btn">
                <Grid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewType('list')} className={`p-2 rounded ${viewType === 'list' ? 'bg-[#F59E0B] text-black' : 'text-[#64748B] hover:text-white'}`} data-testid="view-list-btn">
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="bg-[#121826] rounded-2xl border border-[#1E293B] p-4 animate-pulse">
                <div className="h-12 bg-[#1E293B] rounded mb-3" />
                <div className="h-6 bg-[#1E293B] rounded w-2/3 mb-2" />
                <div className="h-4 bg-[#1E293B] rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : viewType === 'grid' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {securities.map((s) => {
              const isUp = (s.change_percent || 0) >= 0;
              const imgUrl = s.poster_url || s.photo_url;
              return (
                <Link key={s.id} to={`/security/${s.symbol}`} className="bg-[#121826] rounded-2xl border border-[#1E293B] p-4 hover:border-[#F59E0B]/50 hover:shadow-[0_0_20px_rgba(245,158,11,0.1)] transition-all duration-300 group" data-testid={`security-card-${s.symbol}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#1E293B] rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                      {imgUrl ? <img src={imgUrl} alt={s.name} className="w-full h-full object-cover" /> : <span className="text-lg font-bold text-[#F59E0B]/40 font-mono">{s.symbol?.slice(0, 2)}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[#F8FAFC] font-semibold truncate group-hover:text-[#F59E0B] transition-colors text-sm">{s.name}</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-[#F59E0B] text-xs font-mono font-semibold">{s.symbol}</span>
                        <span className="text-[#64748B] text-xs">{s.security_type === 'movie' ? 'Movie' : 'Star'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[#F8FAFC] font-bold text-lg font-mono">B₹{(s.price || 0).toFixed(2)}</span>
                    <span className={`text-sm font-mono font-medium flex items-center gap-1 ${isUp ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {isUp ? '+' : ''}{(s.change_percent || 0).toFixed(2)}%
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="bg-[#121826] rounded-2xl border border-[#1E293B] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#1E293B]">
                <tr>
                  <th className="text-left text-[#64748B] font-medium text-xs uppercase tracking-wider py-3 px-4">Symbol</th>
                  <th className="text-left text-[#64748B] font-medium text-xs uppercase tracking-wider py-3 px-4">Name</th>
                  <th className="text-left text-[#64748B] font-medium text-xs uppercase tracking-wider py-3 px-4">Type</th>
                  <th className="text-right text-[#64748B] font-medium text-xs uppercase tracking-wider py-3 px-4">Price</th>
                  <th className="text-right text-[#64748B] font-medium text-xs uppercase tracking-wider py-3 px-4">Change</th>
                  <th className="text-right text-[#64748B] font-medium text-xs uppercase tracking-wider py-3 px-4">Volume</th>
                  <th className="text-right text-[#64748B] font-medium text-xs uppercase tracking-wider py-3 px-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {securities.map((s) => {
                  const isUp = (s.change_percent || 0) >= 0;
                  return (
                    <tr key={s.id} className="hover:bg-[#1E293B]/50 transition-colors">
                      <td className="py-3 px-4"><span className="text-[#F59E0B] font-mono font-bold">{s.symbol}</span></td>
                      <td className="py-3 px-4 text-[#F8FAFC]">{s.name}</td>
                      <td className="py-3 px-4"><span className="text-[#64748B] text-sm">{s.security_type === 'movie' ? 'MovieStock' : 'StarBond'}</span></td>
                      <td className="py-3 px-4 text-right"><span className="text-[#F8FAFC] font-semibold font-mono">B₹{(s.price || 0).toFixed(2)}</span></td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-mono font-medium flex items-center justify-end gap-1 ${isUp ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                          {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {isUp ? '+' : ''}{(s.change_percent || 0).toFixed(2)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right"><span className="text-[#94A3B8] text-sm font-mono">{(s.volume || 0).toLocaleString()}</span></td>
                      <td className="py-3 px-4 text-right">
                        <Link to={`/security/${s.symbol}`}>
                          <Button variant="ghost" size="sm" className="text-[#F59E0B] hover:text-[#D97706] hover:bg-[#F59E0B]/10">
                            Trade <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > 20 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)} variant="outline" className="border-[#1E293B] text-[#94A3B8]" data-testid="prev-page-btn">Previous</Button>
            <span className="flex items-center px-4 text-[#94A3B8] text-sm">Page {page}</span>
            <Button disabled={securities.length < 20} onClick={() => setPage(p => p + 1)} variant="outline" className="border-[#1E293B] text-[#94A3B8]" data-testid="next-page-btn">Next</Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Securities;
